import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { BuyCredits } from "@/components/BuyCredits";
import { VenmoTopUp } from "@/components/VenmoTopUp";
import { CustomTopUp } from "@/components/CustomTopUp";
import { Bitcoin, CreditCard, Zap, Clock, BadgeCheck, Coins, Package, SlidersHorizontal } from "lucide-react";

// All providers use the same server-authoritative prices.
export function TopUpCredits() {
  const [method, setMethod] = useState("stripe");
  const [mode, setMode] = useState("packs"); // "packs" | "custom"
  const { data: venmoCfg } = useQuery({
    queryKey: ["venmo-config"],
    queryFn: async () => (await api.get("/payments/venmo/config")).data,
  });
  const venmoOn = venmoCfg?.enabled !== false;
  const { data: stripeCfg } = useQuery({ queryKey: ["stripe-config"], queryFn: async () => (await api.get("/payments/stripe/config")).data });
  const { data: pricing } = useQuery({ queryKey: ["credit-pricing"], queryFn: async () => (await api.get("/payments/quote?usd=5")).data });

  const methods = [
    { key: "stripe", label: "Card", accent: "#34d399", icon: <CreditCard className="h-5 w-5 text-emerald-300" />,
      tag: { text: "Stripe", Icon: BadgeCheck }, usage: "Visa, Mastercard and other supported cards. Credits are delivered after payment is verified." },
    {
      key: "crypto", label: "Crypto", accent: "#fbbf24",
      icon: <Bitcoin className="h-5 w-5" style={{ color: "#fbbf24" }} />,
      tag: { text: "Automatic", Icon: Zap },
      usage: "Pay in BTC, ETH, USDT & more via secure checkout. Credits are added automatically ~15–20 min after the payment confirms on-chain — no review, no waiting on us.",
    },
    venmoOn && {
      key: "venmo", label: "Venmo", accent: "#3D95CE",
      icon: <span className="font-display text-[#3D95CE] text-xl leading-none">V</span>,
      tag: { text: "Quick review", Icon: Clock },
      usage: `Send to @${venmoCfg?.handle || "Playden-INC"} on Venmo with the note we give you. An admin verifies the payment and credits your account — you'll get a notification when it lands.`,
    },
  ].filter(Boolean);

  const active = methods.find((m) => m.key === method) || methods[0];

  return (
    <div data-testid="topup-credits" className="hud-pill rounded-2xl p-5 sm:p-6 relative overflow-hidden">
      <div className="absolute inset-0 holo-scan opacity-[0.06] pointer-events-none" />

      {/* Header */}
      <div className="relative flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-amber-400/15 border border-amber-300/30 flex items-center justify-center">
          <Coins className="h-6 w-6 text-amber-300" />
        </div>
        <div>
          <h2 className="font-display text-2xl tracking-[0.04em] text-white">TOP UP CREDITS</h2>
          <p data-testid="topup-payment-methods" className="text-white/50 text-sm">Card, crypto or Venmo. Same packs, same prices.</p>
          <p data-testid="topup-bonus-cap" className="mt-1 text-xs text-emerald-300">{pricing ? `${pricing.base_credits_per_usd.toLocaleString()} credits / $1 · bonuses ${pricing.bonus_tiers.map(t => `${Math.round(t.bonus_pct * 100)}%`).join(" / ")} · maximum +50%` : "Loading current prices…"}</p>
          <p data-testid="topup-powered-by" className="mt-1 text-[11px] text-white/50">Powered by Steele Industries</p>
        </div>
      </div>

      {/* Method selector — doubles as the explainer for each method's usage */}
      <div className="relative grid gap-3 lg:grid-cols-3">
        {methods.map((m) => {
          const selected = active.key === m.key;
          return (
            <button
              key={m.key}
              data-testid={`topup-method-${m.key}`}
              onClick={() => setMethod(m.key)}
              className={`text-left rounded-xl border p-4 transition-all ${selected ? "ring-1" : "opacity-70 hover:opacity-100"}`}
              style={{
                borderColor: selected ? m.accent : "rgba(255,255,255,0.10)",
                background: selected ? `${m.accent}14` : "rgba(255,255,255,0.03)",
                boxShadow: selected ? `0 0 0 1px ${m.accent}55, 0 8px 30px ${m.accent}1f` : "none",
              }}
            >
              <div className="flex items-center gap-2">
                <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: `${m.accent}1f`, border: `1px solid ${m.accent}55` }}>
                  {m.icon}
                </span>
                <span className="font-display text-lg text-white tracking-[0.04em]">{m.label}</span>
                <span className="ml-auto inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.15em] rounded-full px-2 py-0.5"
                  style={{ color: m.accent, background: `${m.accent}1a`, border: `1px solid ${m.accent}3a` }}>
                  <m.tag.Icon className="h-3 w-3" /> {m.tag.text}
                </span>
              </div>
              <p className="text-white/55 text-xs leading-relaxed mt-2">{m.usage}</p>
            </button>
          );
        })}
      </div>

      {/* Packs vs custom amount toggle */}
      <div className="relative mt-5 flex items-center gap-1 p-1 rounded-xl bg-black/30 border border-white/10 w-full sm:w-auto sm:inline-flex">
        {[
          { key: "packs", label: "Quick packs", Icon: Package },
          { key: "custom", label: "Custom amount", Icon: SlidersHorizontal },
        ].map((t) => {
          const on = mode === t.key;
          return (
            <button key={t.key} data-testid={`topup-tab-${t.key}`} onClick={() => setMode(t.key)}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${on ? "bg-white/10 text-white" : "text-white/45 hover:text-white/70"}`}>
              <t.Icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Active method body */}
      <div className="relative mt-5 pt-5 border-t border-white/10">
        {mode === "custom"
          ? <CustomTopUp key={active.key} method={active.key} venmoHandle={venmoCfg?.handle} unavailable={active.key === "stripe" && !stripeCfg?.enabled} />
          : active.key !== "venmo" ? <BuyCredits compact bare provider={active.key} unavailable={active.key === "stripe" && !stripeCfg?.enabled} /> : <VenmoTopUp bare />}
      </div>

      <div className="relative mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-white/35">
        <span className="inline-flex items-center gap-1.5"><BadgeCheck className="h-3.5 w-3.5 text-emerald-300/70" /> Same credit packs whichever you choose.</span>
        <span data-testid="topup-processing-methods" className="inline-flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-amber-300/70" /> Card and crypto verification · Venmo admin review.</span>
        <span className="text-amber-200/70">All top-ups are final — no refunds.</span>
      </div>
    </div>
  );
}
